import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lightning, Eye, EyeSlash, Envelope, Lock, User } from '@phosphor-icons/react';

function formatApiError(detail) {
  if (detail == null) return 'Si è verificato un errore. Riprova.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default function Landing() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMsg, setResetMsg] = useState('');
  const [resetError, setResetError] = useState('');
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetMsg('');
    setResetLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        email: resetEmail,
        new_password: resetPassword
      });
      setResetMsg('Password aggiornata! Ora puoi accedere.');
      setTimeout(() => {
        setShowReset(false);
        setResetMsg('');
        setEmail(resetEmail);
      }, 1500);
    } catch (err) {
      setResetError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setResetLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(email, password);
      } else {
        await register(email, password, name);
      }
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[#05050A]" />
      <div 
        className="absolute inset-0 opacity-15"
        style={{
          backgroundImage: `url('/bg-grid.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      />
      
      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="glass border-b border-white/10 px-6 py-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FF2A85] flex items-center justify-center neon-glow">
                <Lightning weight="fill" className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Unbounded' }}>
                v<span className="text-[#FF2A85]">ai</span>online
              </span>
            </div>
          </div>
        </header>

        {/* Main */}
        <main className="flex-1 flex items-center justify-center px-6 py-6 lg:py-12">
          <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left - Hero */}
            <div className="text-center lg:text-left">
              <h1 
                className="text-3xl sm:text-5xl lg:text-6xl font-black tracking-tighter mb-4"
                style={{ fontFamily: 'Unbounded' }}
              >
                <span className="text-white">Da Volantino a</span>
                <br />
                <span className="gradient-text">Campagna Digitale</span>
              </h1>
              <p className="text-base lg:text-lg text-white/90 mb-6 max-w-lg mx-auto lg:mx-0" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}>
                Manda online la tua campagna pubblicitaria offline. Più clienti per te, in un solo click.
              </p>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <div className="flex items-center gap-2 text-sm text-white/80">
                  <div className="w-2 h-2 rounded-full bg-[#E4FF1A]" />
                  Analisi AI del volantino
                </div>
                <div className="flex items-center gap-2 text-sm text-white/80">
                  <div className="w-2 h-2 rounded-full bg-[#E4FF1A]" />
                  Copy automatico in italiano
                </div>
                <div className="flex items-center gap-2 text-sm text-white/80">
                  <div className="w-2 h-2 rounded-full bg-[#E4FF1A]" />
                  Formati 1:1 e 9:16
                </div>
              </div>
            </div>

            {/* Right - Auth Form */}
            <div className="w-full max-w-md mx-auto">
              <div className="bg-[#0C0D14] border border-white/10 rounded-2xl p-6 lg:p-8">
                {/* Tabs */}
                <div className="flex mb-6 bg-black/40 rounded-lg p-1">
                  <button
                    data-testid="login-tab"
                    onClick={() => setIsLogin(true)}
                    className={`flex-1 py-2.5 px-4 rounded-md text-sm font-semibold transition-all ${
                      isLogin 
                        ? 'bg-[#FF2A85] text-white' 
                        : 'text-[#A1A1AA] hover:text-white'
                    }`}
                  >
                    Accedi
                  </button>
                  <button
                    data-testid="register-tab"
                    onClick={() => setIsLogin(false)}
                    className={`flex-1 py-2.5 px-4 rounded-md text-sm font-semibold transition-all ${
                      !isLogin 
                        ? 'bg-[#FF2A85] text-white' 
                        : 'text-[#A1A1AA] hover:text-white'
                    }`}
                  >
                    Registrati
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {!isLogin && (
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-sm text-[#A1A1AA]">Nome</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                        <Input
                          id="name"
                          data-testid="name-input"
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Il tuo nome"
                          className="pl-10 bg-black border-white/10 focus:border-[#0055FF]"
                          required={!isLogin}
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm text-[#A1A1AA]">Email</Label>
                    <div className="relative">
                      <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                      <Input
                        id="email"
                        data-testid="email-input"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="email@esempio.com"
                        className="pl-10 bg-black border-white/10 focus:border-[#0055FF]"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm text-[#A1A1AA]">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                      <Input
                        id="password"
                        data-testid="password-input"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="La tua password"
                        className="pl-10 pr-10 bg-black border-white/10 focus:border-[#0055FF]"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
                      >
                        {showPassword ? <EyeSlash className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  {error && (
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                      {error}
                    </div>
                  )}

                  <Button
                    type="submit"
                    data-testid="submit-button"
                    disabled={loading}
                    className="w-full bg-[#FF2A85] hover:bg-[#FF4D9B] text-white font-semibold py-3 btn-glow transition-all duration-300"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {isLogin ? 'Accesso in corso...' : 'Registrazione...'}
                      </span>
                    ) : (
                      isLogin ? 'Accedi' : 'Crea Account'
                    )}
                  </Button>
                  {isLogin && (
                    <button
                      type="button"
                      data-testid="forgot-password-link"
                      onClick={() => { setShowReset(true); setResetEmail(email); setResetError(''); setResetMsg(''); }}
                      className="w-full text-center text-sm text-[#A1A1AA] hover:text-[#FF2A85] transition-colors mt-2"
                    >
                      Hai dimenticato la password?
                    </button>
                  )}
                </form>
              </div>
            </div>
          </div>
        </main>

        {/* Password Reset Modal */}
        {showReset && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowReset(false)} />
            <div className="relative bg-[#0C0D14] border border-white/10 rounded-2xl p-6 w-full max-w-sm">
              <button
                data-testid="close-reset-modal"
                onClick={() => setShowReset(false)}
                className="absolute top-4 right-4 text-[#A1A1AA] hover:text-white"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
              <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'Unbounded' }}>Reset Password</h3>
              <p className="text-sm text-[#A1A1AA] mb-4">Inserisci la tua email e la nuova password</p>

              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email" className="text-sm text-[#A1A1AA]">Email</Label>
                  <div className="relative">
                    <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                    <Input
                      id="reset-email"
                      data-testid="reset-email-input"
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      placeholder="La tua email"
                      className="pl-10 bg-black border-white/10 focus:border-[#0055FF]"
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reset-password" className="text-sm text-[#A1A1AA]">Nuova Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
                    <Input
                      id="reset-password"
                      data-testid="reset-password-input"
                      type="password"
                      value={resetPassword}
                      onChange={(e) => setResetPassword(e.target.value)}
                      placeholder="Nuova password (min 6 caratteri)"
                      className="pl-10 bg-black border-white/10 focus:border-[#0055FF]"
                      required
                      minLength={6}
                    />
                  </div>
                </div>

                {resetError && (
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{resetError}</div>
                )}
                {resetMsg && (
                  <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 text-green-400 text-sm">{resetMsg}</div>
                )}

                <Button
                  type="submit"
                  data-testid="reset-password-submit"
                  disabled={resetLoading}
                  className="w-full bg-[#FF2A85] hover:bg-[#FF4D9B] text-white font-semibold py-3"
                >
                  {resetLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Aggiornamento...
                    </span>
                  ) : 'Aggiorna Password'}
                </Button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
